import { getBaseUrl, request } from 'src/helper/api';
import { guessTz } from 'src/helper/date';

const userTimezoneHeader = { 'X-timezone': guessTz() };

/**
 * Retrieves the activity logs of a specific service request.
 * @param {string} [ServiceRequestId] - The ID of the service request to retrieve activity logs for.
 * @param {string} [token] - Optional authentication token.
 * @param {AbortSignal} [signal] - Optional abort signal to cancel the request.
 * @returns {Promise<AxiosResponse<ServiceRequestAPIResponseObj[]>>} - A promise that
 *  resolves to the response of the request.
 */
export const getSrActivityLogs = (
  ServiceRequestId?: string,
  token?: string,
  signal?: AbortSignal,
) =>
  request(
    {
      method: 'GET',
      url: `${process.env.NEXT_PUBLIC_MOCK_BASE_URL || getBaseUrl()}api/service-requests/${ServiceRequestId}/activity-logs`,
      contentType: 'json',
      token,
      signal,
      extraHeaders: userTimezoneHeader,
    },
    'activity-log/getSrActivityLogs',
  );

export const getWoActivityLogs = (workOrderId?: string, token?: string, signal?: AbortSignal) =>
  request(
    {
      method: 'GET',
      url: `${getBaseUrl()}api/work-orders/${workOrderId}/activity-logs`,
      contentType: 'json',
      token,
      signal,
      extraHeaders: userTimezoneHeader,
    },
    'activity-log/getActivityLogs',
  );

export const getActivityLogDetails = (token?: string, signal?: AbortSignal, params?: any) =>
  request(
    {
      method: 'GET',
      url: `${getBaseUrl()}api/activity-logs/details`,
      contentType: 'json',
      token,
      signal,
      params,
    },
    'activity-log/getActivityLogDetails',
  );

// START - Internal note add/edit/delete api routes
// used for both service request and work orders

export const updateInternalNote = (
  entityPath: 'work-orders' | 'service-requests' | '',
  entityId: string,
  noteId: string,
  token?: string,
) =>
  request(
    {
      method: 'PUT',
      url: `${getBaseUrl()}api/${entityPath}/${entityId}/notes/${noteId}`,
      contentType: 'json',
      token,
      shouldAddSocketId: true,
    },
    'activity-log/updateInternalNote',
  );

export const addNewInternalNote = (
  entityPath: 'work-orders' | 'service-requests' | '',
  entityId: string,
  token?: string,
) =>
  request(
    {
      method: 'POST',
      url: `${getBaseUrl()}api/${entityPath}/${entityId}/notes`,
      contentType: 'json',
      token,
      shouldAddSocketId: true,
    },
    'activity-log/addNewInternalNote',
  );

export const deleteInternalNote = (
  entityPath: 'work-orders' | 'service-requests' | '',
  entityId: string,
  noteId?: string,
  token?: string,
) =>
  request(
    {
      method: 'DELETE',
      url: `${getBaseUrl()}api/${entityPath}/${entityId}/notes/${noteId}`,
      contentType: 'json',
      token,
      shouldAddSocketId: true,
    },
    'activity-log/deleteInternalNote',
  );

// END - internal note

// ============================================================
// SUMMARY APIs
// ============================================================

/**
 * Retrieves the AI-generated summary for a specific service request.
 * @param {string} [token] - Optional authentication token.
 * @param {string} [serviceRequestId] - The ID of the service request.
 * @param {AbortSignal} [signal] - Optional abort signal to cancel the request.
 */
export const getServiceRequestSummary = (
  token?: string,
  serviceRequestId?: string,
  signal?: AbortSignal,
  params?: any,
) =>
  request(
    {
      method: 'POST',
      url: `${getBaseUrl()}api/service-requests/${serviceRequestId}/maintenance-ai-summary`,
      params,
      contentType: 'json',
      token,
      signal,
    },
    'activity-log/getServiceRequestSummary',
  );
